import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import { Options } from '@popperjs/core';
import {
  GetOptionsFn,
  IOptionGroup,
  ISelectStatePropsValue,
  isIOptionGroup,
  SelectSearchable,
  SelectSearchableVariant,
} from '@hse-design/core';
import { SelectPropsWithoutRef } from '../../common/types';
import { useSelectState } from './useSelectState';
import { SelectOptionGroup } from './SelectOptionGroup';
import { SelectOption } from './SelectOption';
import { SelectSearchInput } from './SelectSearchInput';
import { SelectDropdown } from './SelectDropdown';
import { SelectValueContainer } from './SelectValueContainer';
import { DropdownMenuBaseItemProps } from '../DropdownMenu/DropdownMenuBaseItem';
import { ISelectOption } from './types';
import { DropdownMenuDivider } from '../DropdownMenu';

export enum SelectWidthVariant {
  auto = 'auto',
  full = 'full'
}

export type SelectWidth = SelectWidthVariant | number;

export enum SelectSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export enum SelectVariant {
  default = 'default',
  borderless = 'borderless'
}

export { SelectSearchableVariant, SelectSearchable };

export type ISelectProps = Omit<
  SelectPropsWithoutRef,
  'onChange' | 'onKeyDown' | 'onFocus' | 'onBlur' | 'size' | 'value'
> & {
  /**
   * Массив опций и/или групп опций
   */
  options?: (ISelectOption | IOptionGroup<ISelectOption>)[];
  /**
   * Размер Select ("small", "medium", "large")
   */
  size?: SelectSize;
  /**
   * Устанавливает, является ли меню постоянно открытым
   */
  permanent?: boolean;
  /**
   * Устанавливает вариант отображения поля поиска по опциям Select:
   * - false - поиск отключен
   * - "dropdown" - поля поиска отображается вверху дропдауна
   * - "inline" - поле поиска находится в контейнере значения
   */
  searchable?: SelectSearchable;
  /**
   * Максимальное количество строк текста у опции для отображения. По умолчанию две строки
   */
  lineClamp?: DropdownMenuBaseItemProps['lineClamp'];
  /**
   * Включает неактивное состояние Select - состояние, при котором компонент отображается, но недоступен для действий пользователя
   */
  disabled?: boolean;
  /**
   * Устанавливает, отображаются ли чекбоксы в опциях (для множественного выбора)
   */
  checkboxes?: boolean;
  /**
   * Задаёт произвольную иконку для отображения в левой части опции.
   * Конфликтует с checkboxes.
   *
   * @type React.ElementType
   */
  leftIcon?: React.ElementType;
  /**
   * Текст сообщения, отображаемого в DropdownMenu в случае, если не найдено опций, удовлетворяющих запросу для поиска
   */
  nothingFoundPlaceholder?: string;
  /**
   * Текст сообщения, отображаемого в DropdownMenu во время динамической загрузки опций
   */
  loadingPlaceholder?: string;
  /**
   * Текст плэйсхолдера для поля поиска по опциям
   */
  searchPlaceholder?: string;
  /**
   * Текст, который по умолчанию выводится, если случилась ошибка в getOptions,
   * которая не содержит поле `message`.
   */
  defaultErrorMessage?: string;
  /**
   * Показывать выбранные значения сверху
   */
  selectedFirst?: boolean;
  /**
   * Функция для динамического получения опций. Принимает на вход текущее значение для поиска,
   * возвращает это значение и промис, разрешающийся в массив опций, удовлетворяющих запросу для поиска.
   *
   * При использовании getOptions встроенная фильтрация опций не применяется,
   * так как эта функция предназначена для получения уже отфильтрованного списка
   * из внешнего источника (например, из запроса на бекенд).
   *
   * Если запрос к бекенду возвращает полный перечень допустимых значений
   * и не поддерживает фильтрацию, то не используйте getOptions и передайте
   * этот перечень через проп options.
   */
  getOptions?: GetOptionsFn<ISelectOption>;
  /**
   * Добавляет иконку и возможность очистки текущего значения.
   * При нажатии на иконку вызывается колбек onClear,
   * а если он не был передан, то onChange.
   */
  clearable?: boolean;
  /**
   * Обработчик события нажатия клавиши на компоненте Select
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Обработчик события приобретения фокуса компонентом Select
   */
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  /**
   * Обработчик события потери фокуса компонентом Select
   */
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  /**
   * Ширина компонента
   */
  width?: SelectWidth;
  /**
   * Вариант отображения
   */
  variant?: SelectVariant;
  /**
   * Сообщение об ошибке
   */
  errorMessage?: string;
  /**
   * Дополнительные классы элементов:
   * * valueContainer – контейнер текущего значения
   * * leftIcon – класс иконки leftIcon
   * * value – класс элемента с текущим значением
   * * clearIcon – класс иконки очистки
   * * dropDownMenu – класс контейнера дропдауна
   * * dropDownMenuMessage – класс контейнера сообщения дропдауна
   */
  classes?: {
    valueContainer?: string;
    leftIcon?: string;
    value?: string;
    clearIcon?: string;
    dropdownMenu?: string;
    dropdownMenuMessage?: string;
  };
  className?: string;
} & ISelectStatePropsValue<ISelectOption>;

// eslint-disable-next-line
const noop = () => {};

const emptyOptions = [];
const popperOptions: Partial<Options> = {
  placement: 'bottom',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 8]
      }
    }
  ]
};

export function Select(props: ISelectProps) {
  const {
    classes,
    className,
    options = emptyOptions,
    permanent,
    searchable,
    multiple,
    disabled,
    clearable,
    onClear,
    getOptions,
    value,
    size = SelectSize.medium,
    leftIcon,
    placeholder = 'Выберите значение',
    searchPlaceholder = 'Поиск',
    loadingPlaceholder = 'Загрузка...',
    nothingFoundPlaceholder = 'Ничего не найдено',
    defaultErrorMessage = 'Ничего не найдено',
    checkboxes,
    style,
    errorMessage,
    width,
    variant,
    lineClamp,
    selectedFirst,
    onChange,
    onChangeOption,
    ...innerProps
  } = props;

  const [
    state,
    {
      containerRef,
      searchInputRef,
      dropdownRef,
      multipleValuesRef,
      dropdownMenuItemsRef,
      dropdownMenuItemsContainerRef
    },
    handlers,
    groupedOptions,
    mixinRef
  ] = useSelectState({
    options,
    rootHandlers: innerProps,
    permanent,
    selectedFirst,
    searchable,
    clearable,
    getOptions,
    defaultErrorMessage,
    // getting around typescript
    ...(props.multiple
      ? {
          multiple: props.multiple,
          value: props.value,
          onChange: props.onChange,
          onChangeOption: props.onChangeOption,
          onClear: props.onClear
        }
      : {
          multiple: props.multiple,
          value: props.value,
          onChange: props.onChange,
          onChangeOption: props.onChangeOption,
          onClear: props.onClear
        })
  });

  const focused = useMemo(
    () => !disabled && (state.rootFocused || state.searchInputFocused),
    [disabled, state.rootFocused, state.searchInputFocused]
  );

  const {
    showTags,
    showInputInValueField,
    showSelectedValueInField,
    showClearIcon,
    showNothingFoundPlaceholder,
    showPlaceholderInField
  } = mixinRef.computed;

  const message = state.error
    ? errorMessage || state.errorMessage
    : state.loading
    ? loadingPlaceholder
    : showNothingFoundPlaceholder
    ? nothingFoundPlaceholder
    : null;
  const singleValueLabel =
    (!props.multiple && state.selectedOptions[0]?.label) || null;
  const widthStyles = useMemo(
    () =>
      typeof width === 'number'
        ? {
            width: `${width}px`,
            minWidth: `${width}px`
          }
        : {},
    [width]
  );
  const valueContainerRef = useRef<HTMLDivElement | null>(null);

  const { styles, attributes, update } = usePopper(
    valueContainerRef.current,
    dropdownRef.current,
    popperOptions
  );

  useEffect(() => {
    if (state.menuOpen) update?.();
  }, [state.menuOpen]);

  const renderOption = (option: ISelectOption, key: number) => {
    const selected = state.selectedOptions.some(
      (o) => o.value === option.value
    );
    return (
      <SelectOption
        key={key}
        ref={(el) => {
          dropdownMenuItemsRef.current[key] = el;
        }}
        selected={!(checkboxes && multiple) && selected}
        checked={checkboxes && selected}
        focused={
          !!state.focusedOption && option.value === state.focusedOption.value
        }
        disabled={option.inactive}
        leftIcon={option.leftIcon}
        rightIcon={option.rightIcon}
        hasCheckbox={checkboxes && multiple}
        lineClamp={lineClamp}
        onClick={
          option.inactive
            ? undefined
            : handlers.createOptionClickHandler(option)
        }
        onMouseEnter={
          option.inactive
            ? undefined
            : handlers.createOptionMouseEnterHandler(option)
        }
      >
        {option.label}
      </SelectOption>
    );
  };

  const renderOptions = () => {
    let i = 0;
    const result: JSX.Element[] = [];
    for (const option of groupedOptions) {
      if (isIOptionGroup<ISelectOption>(option)) {
        const group = option;
        result.push(
          <SelectOptionGroup title={group.label} key={i++}>
            {group.options.map((opt) => {
              return renderOption(opt, i++);
            })}
          </SelectOptionGroup>
        );
      } else {
        if (option.divided) {
          result.push(<DropdownMenuDivider key={i++} />);
        }
        result.push(renderOption(option, i++));
      }
    }
    return result;
  };

  const renderSearchInput = ({ placeholder }: { placeholder: string }) => {
    return searchable ? (
      <SelectSearchInput
        type={searchable}
        placeholder={placeholder}
        inputRef={searchInputRef}
        value={state.searchValue}
        onChange={handlers.onSearchChange}
        onFocus={handlers.onSearchInputFocus}
        onBlur={handlers.onSearchInputBlur}
      />
    ) : null;
  };

  const hiddenNativeSelect = (
    <select
      tabIndex={-1}
      multiple={multiple}
      disabled={disabled}
      value={
        multiple
          ? state.selectedOptions.map((o) => String(o.value))
          : state.selectedOptions[0]?.value || ''
      }
      placeholder={placeholder}
      className="hse-Select__input"
      {...innerProps}
      onKeyDown={undefined}
      onFocus={undefined}
      onBlur={undefined}
      onChange={noop}
    >
      {state.filteredOptions.map((option, iOption) => (
        <option value={option.value} key={iOption}>
          {option.label}
        </option>
      ))}
    </select>
  );

  return (
    <div
      ref={containerRef}
      tabIndex={disabled ? -1 : 0}
      className={clsx(className, 'hse-Select', {
        [`hse-Select_variant_${variant}`]: variant,
        'hse-Select_opened': state.menuOpen,
        [`hse-Select_width_${width}`]:
          width === SelectWidthVariant.auto || width === SelectWidthVariant.full
      })}
      style={{ ...style, ...widthStyles }}
      onKeyDown={disabled ? undefined : handlers.onKeyDown}
      onFocus={disabled ? undefined : handlers.onFocus}
      onBlur={disabled ? undefined : handlers.onBlur}
    >
      {hiddenNativeSelect}
      <SelectValueContainer
        ref={valueContainerRef}
        multipleValuesRef={multipleValuesRef}
        className={classes?.valueContainer}
        focused={focused}
        disabled={disabled}
        size={size}
        leftIcon={leftIcon}
        selectedOptions={state.selectedOptions}
        showTags={showTags}
        focusedTagI={state.focusedTagI}
        showSelectedValueInField={showSelectedValueInField}
        showPlaceholderInField={showPlaceholderInField}
        showInputInValueField={showInputInValueField}
        renderSearchInput={renderSearchInput}
        singleValueLabel={singleValueLabel}
        placeholder={placeholder}
        showClearIcon={!!showClearIcon}
        clearButtonFocused={state.clearButtonFocused}
        isOpen={state.menuOpen}
        onClearClick={handlers.onClearClick}
        onClearFocus={handlers.onClearFocus}
        onClearBlur={handlers.onClearBlur}
        onClick={disabled ? undefined : handlers.onValueClick}
      />
      {!disabled && (
        <SelectDropdown
          classes={{ dropdownMenuMessage: classes?.dropdownMenuMessage }}
          className={classes?.dropdownMenu}
          ref={dropdownRef}
          itemsRef={dropdownMenuItemsContainerRef}
          isOpen={state.menuOpen}
          style={styles.popper}
          header={
            searchable === 'dropdown' &&
            renderSearchInput({
              placeholder: searchPlaceholder
            })
          }
          message={message}
          searchable={searchable}
          {...attributes.popper}
        >
          {state.menuOpen && !message && renderOptions()}
        </SelectDropdown>
      )}
    </div>
  );
}

Select.Size = SelectSize;
Select.Width = SelectWidthVariant;
Select.Variant = SelectVariant;
Select.Searchable = SelectSearchableVariant;
